// 牌桌主体：实体场地、主题色棋子、完整卡面抽屉与事件驱动动画。
import type { CSSProperties } from "react";
import { DieShape } from "./actions.tsx";
import type { ObjectAction } from "./actions.tsx";
import { AnimatedNumber } from "./animated-number.tsx";
import "./dice.css";

import { CardBack, CardFace, tip } from "./assets.tsx";
import { PlaceCard, PlayerSeal, kanjiNumber } from "./cards.tsx";
import { cardName } from "./glossary.ts";
import { placeDropId, playerDropId, useDrag } from "./drag.tsx";
import type { DropPayload } from "./drag.tsx";
import { cardAccent, playerTheme, playerThemeStyle } from "./themes.ts";
import type {
  DeckState,
  GameEvent,
  PlayerState,
  RoleCard,
  Room,
  State,
} from "./types.ts";

type DragStart = (event: React.PointerEvent, payload: DropPayload) => void;

function eventRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function eventText(data: Record<string, unknown>, key: string): string {
  return typeof data[key] === "string" ? data[key] : "";
}

function eventNumber(data: Record<string, unknown>, key: string): number {
  return typeof data[key] === "number" ? data[key] : 0;
}

export function Board({
  state,
  lastEvent,
  onPickSlot,
  pickSlots,
  placeActions,
  playerActions,
  onObjectAction,
  onDropTap,
  onPlayerPick,
}: {
  state: State;
  lastEvent: GameEvent | null;
  pickSlots?: number[] | undefined;
  placeActions: ReadonlyMap<number, ObjectAction[]>;
  playerActions: ReadonlyMap<string, ObjectAction[]>;
  onPickSlot?: ((slot: number) => void) | undefined;
  onObjectAction: (action: ObjectAction) => void;
  onDropTap: (id: string) => void;
  onPlayerPick: (playerId: string) => void;
}) {
  const drag = useDrag();
  const byId = new Map(state.players.map((player) => [player.id, player]));
  const playerIndex = new Map(state.players.map((player, index) => [player.id, index]));
  const turnSlot = byId.get(state.turn.player)?.slot ?? -1;
  const movingPlayer =
    lastEvent?.type === "move" ? eventText(eventRecord(lastEvent.data), "player") : "";

  return (
    <div className="board">
      {state.board.ranges.map((pair, index) => (
        <section className={`area area-${index}`} key={`${pair[0]}-${pair[1]}`}>
          {pair.map((slot) => {
            const place = state.board.places.find((entry) => entry.slot === slot);
            if (!place) return null;
            const occupants = place.players
              .map((id) => byId.get(id))
              .filter((player): player is PlayerState => player !== undefined);
            const dropId = placeDropId(slot);
            return (
              <div className="board-place" key={slot}>
                <PlaceCard
                  place={place}
                  occupants={occupants}
                  playerIndex={playerIndex}
                  current={turnSlot === slot}
                  droppable={drag?.payload.actions.has(dropId) ?? false}
                  onDropTap={() => onDropTap(dropId)}
                  pickable={pickSlots?.includes(slot) ?? false}
                  onPick={onPickSlot ? () => onPickSlot(slot) : undefined}
                  actions={placeActions.get(slot) ?? []}
                  onAction={onObjectAction}
                  playerActions={playerActions}
                  onPlayerAction={onObjectAction}
                  onPlayerPick={onPlayerPick}
                  movingPlayer={movingPlayer}
                />
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

const TURN_PHASES: Record<string, string> = { move: "移动", area: "场所", attack: "攻击" };

function turnInstruction(state: State): string {
  if (state.turn.step === "move") {
    return "掷一颗 D4 与一颗 D6；点数合计决定目的地，合计 7 时可任选场所。";
  }
  if (state.turn.step === "area") {
    return "移动已完成。点击当前场地、牌堆或玩家执行场所行动；不使用则明确跳过。";
  }
  if (state.turn.step === "attack") {
    return "点击高亮玩家发动攻击；不攻击则结束回合。通常伤害取 |D6 − D4|。";
  }
  return "按桌面高亮对象继续当前结算。";
}


export function TableStatus({
  state,
  room,
  notice,
}: {
  state: State;
  room: Room | null;
  notice: string | null;
}) {
  const nameOf = (id: string): string =>
    room?.players.find((player) => player.player_id === id)?.name ??
    state.players.find((player) => player.id === id)?.name ??
    "未知玩家";
  const rawTurnIndex = state.players.findIndex((player) => player.id === state.turn.player);
  const finished = room?.phase === "finished" || state.phase === "finished";
  const extraTurnParts = [
    state.turn.extra_turns > 0 ? `剩余额外回合 ${state.turn.extra_turns}` : "",
    state.turn.extra_turn_queue?.length ? `队列 ${state.turn.extra_turn_queue.map(nameOf).join(" → ")}` : "",
    state.turn.extra_turn_resume ? `随后恢复 ${nameOf(state.turn.extra_turn_resume)}` : "",
  ].filter(Boolean);
  return (
    <p className="table-status" style={playerThemeStyle(Math.max(0, rawTurnIndex))}>
      <span className="status-round">第 <AnimatedNumber value={state.round} /> 回合</span>
      <span className="status-phase">{finished ? "终局" : `${TURN_PHASES[state.turn.step] ?? state.turn.step}阶段`}</span>
      {!finished && (
        <span className="status-turn">
          {rawTurnIndex >= 0 && <span className="status-piece" aria-hidden="true">{playerTheme(rawTurnIndex).piece}</span>}
          <strong>{rawTurnIndex >= 0 ? nameOf(state.turn.player) : "开局落位"}</strong> 执子
        </span>
      )}
      <span className="status-guidance">{finished ? "对局结束，全员身份已公开。" : notice ?? turnInstruction(state)}</span>
      {extraTurnParts.length > 0 && <small className="status-extra-turns">{extraTurnParts.join(" · ")}</small>}
    </p>
  );
}



export function DeckRow({
  decks,
  payloads,
  onDragStart,
}: {
  decks: Record<string, DeckState>;
  payloads: Map<string, DropPayload>;
  onDragStart: DragStart;
}) {
  const order = ["hakurei", "moriya", "kleinji"];
  const label: Record<string, string> = {
    hakurei: "博麗神社",
    moriya: "守矢神社",
    kleinji: "命蓮寺",
  };
  return (
    <div className="decks">
      {order.map((key) => {
        const deck = decks[key];
        if (!deck) return null;
        const payload = payloads.get(key);
        const accentSource = key === "hakurei" ? "hakurei_sake" : key === "moriya" ? "moriya_underground_rose" : "kleinji_namusam";
        return (
          <div className="deck-slot" key={key}>
            <div
              className={`deck deck-${key}${payload ? " is-draggable" : ""}`}
              data-deck={key}
              onPointerDown={payload ? (event) => onDragStart(event, payload) : undefined}
              {...tip({
                title: label[key] ?? "卡组",
                kicker: "山札",
                summary: `剩余 ${deck.remaining} 张`,
                detail: `弃牌 ${deck.discard_visible ? `${deck.discard} 张` : "背面不可见"}\n${
                  payload?.label ?? "当前阶段不能从这副牌堆抽牌"
                }`,
                color: cardAccent(accentSource),
              })}
            >
              <span className="deck-edge edge-one" aria-hidden="true" />
              <span className="deck-edge edge-two" aria-hidden="true" />
              <CardBack deck={key} className="deck-material" />
              <span className="deck-name">{label[key] ?? "卡組"}</span>
              <span className="deck-count"><AnimatedNumber value={deck.remaining} /></span>
            </div>
            <span
              className={`discard-pile${deck.discard > 0 ? " has-cards" : ""}`}
              data-discard-deck={key}
              {...tip({
                title: `${label[key] ?? "卡组"}弃牌堆`,
                summary: deck.discard_visible ? `${deck.discard} 张` : "背面不可见",
                color: cardAccent(accentSource),
              })}
            >
              {deck.discard > 0 && <CardBack deck={key} />}
              <i aria-hidden="true">棄</i>
              <b>{deck.discard_visible ? <AnimatedNumber value={deck.discard} /> : "?"}</b>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function PlayerPanel({
  player,
  themeIndex,
  isTurn,
  privateRole,
  position,
  seatFraction = 0.5,
  isMe,
  onDropTap,
  sealPayload,
  actions,
  onAction,
  onDragStart,
  onPick,
  pinned,
  revealAction,
}: {
  player: PlayerState;
  themeIndex: number;
  isTurn: boolean;
  privateRole: RoleCard | null;
  position: string;
  seatFraction?: number;
  isMe: boolean;
  onDropTap: (id: string) => void;
  sealPayload: DropPayload | null;
  actions: ObjectAction[];
  onAction: (action: ObjectAction) => void;
  onDragStart: DragStart;
  onPick: () => void;
  pinned: boolean;
  revealAction: ObjectAction | null;
}) {
  const drag = useDrag();
  const dropId = playerDropId(player.id);
  return (
    <article
      style={{ ...playerThemeStyle(themeIndex), "--seat-fraction": seatFraction } as CSSProperties}
      data-seat-position={position}
      data-player-id={player.id}
      className={[
        "player-panel",
        isTurn ? "is-turn" : "",
        isMe ? "is-me" : "",
        pinned ? "is-pinned" : "",
        player.alive ? "" : "is-fallen",
        actions.length > 0 ? "is-targetable" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => {
        if (actions.length === 1) onAction(actions[0]!);
        else if (isMe && revealAction) return;
        else onPick();
      }}
    >
      <PlayerSeal
        player={player}
        themeIndex={themeIndex}
        isTurn={isTurn}
        isMe={isMe}
        privateRole={privateRole}
        droppable={drag?.payload.actions.has(dropId) ?? false}
        onDropTap={() => onDropTap(dropId)}
        onDragStart={isMe && sealPayload ? (event) => onDragStart(event, sealPayload) : undefined}
        onReveal={isMe && revealAction ? () => onAction(revealAction) : undefined}
      />
      {actions.length > 1 && (
        <div className="object-choices player-object-choices" aria-label={`${player.name}的可执行行动`}>
          {actions.map((action) => (
            <button
              type="button"
              key={`${action.label}-${JSON.stringify(action.action)}`}
              onClick={(event) => {
                event.stopPropagation();
                onAction(action);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </article>
  );
}

export function LogPanel({
  log,
}: {
  log: { key: string; seq: number; hidden: boolean; text: string }[];
}) {
  return (
    <section className="log-panel">
      <h2 className="section-title">巻物</h2>
      <ol className="log-list">
        {[...log].reverse().map((entry) => (
          <li className={entry.hidden ? "is-hidden" : ""} key={entry.key}>
            <span className="log-seq">{kanjiNumber(entry.seq % 13)}</span>
            {entry.hidden && <span className="log-seal">内</span>}
            {entry.text}
          </li>
        ))}
        {log.length === 0 && <li className="is-empty">尚无记载</li>}
      </ol>
    </section>
  );
}





function visualEventKey(event: GameEvent): string {
  return `${event.seq}|${event.type}|${JSON.stringify(event.data ?? {})}`;
}



function DicePair({ d4, d6, waiting = false }: { d4: number; d6: number; waiting?: boolean }) {
  const label = [
    d4 > 0 ? `四面骰 ${d4}` : "",
    d6 > 0 ? `六面骰 ${d6}` : "",
  ].filter(Boolean).join("，");
  return (
    <div className="dice-pair" aria-label={waiting ? "等待掷骰结果" : label}>
      {(waiting || d4 > 0) && (
        <span className="effect-die die-d4">
          <DieShape kind="d4" value={waiting ? "" : d4} showKind />
        </span>
      )}
      {(waiting || (d4 > 0 && d6 > 0)) && <span className="effect-plus">＋</span>}
      {(waiting || d6 > 0) && (
        <span className="effect-die die-d6">
          <DieShape kind="d6" value={waiting ? "" : d6} showKind />
        </span>
      )}
    </div>
  );
}

function DiceWindow({
  playerName, d4, d6, label, className = "", onRoll, rollLabel, phase = "result", diceKey,
}: {
  playerName: string; d4: number; d6: number; label: string;
  className?: string; onRoll?: () => void; rollLabel?: string;
  phase?: "ready" | "rolling" | "result"; diceKey?: string;
}) {
  const content = <>
    <span className="effect-player">{playerName}</span>
    <DicePair key={diceKey} d4={d4} d6={d6} waiting={phase !== "result"} />
    <strong>{label}</strong>
  </>;
  return (
    <section
      className={`sequence-layer sequence-dice continuous-dice ${className} is-${phase}`}
      aria-label={phase === "result" ? "投掷结果" : "掷骰区域"} aria-live="polite"
      aria-busy={phase === "rolling"}
    >
      <button
        type="button" className="dice-tray" onClick={phase === "ready" ? onRoll : undefined}
        disabled={phase !== "ready"} aria-label={phase === "ready" ? rollLabel ?? label : undefined}
      >{content}</button>
    </section>
  );
}

export function TableEffects({ event, state, diceOutcomes, roll }: {
  event: GameEvent | null;
  state: State;
  diceOutcomes: GameEvent[];
  roll?: { playerName: string; label: string; actionLabel: string; rolling: boolean; onRoll: () => void } | null;
}) {
  if (roll && (!event || !roll.rolling)) return (
    <DiceWindow
      playerName={roll.playerName} d4={0} d6={0}
      label={roll.rolling ? "投掷中…" : roll.label}
      phase={roll.rolling ? "rolling" : "ready"} onRoll={roll.onRoll}
      rollLabel={roll.actionLabel} diceKey="pending"
    />
  );
  if (!event) return null;
  const eventKey = visualEventKey(event);
  const data = eventRecord(event.data);
  const playerId = eventText(data, "player");
  const player = state.players.find((entry) => entry.id === playerId);
  const playerName = player?.name ?? "";

  if (event.type === "game_started") {
    const center = (state.players.length - 1) / 2;
    return (
      <div key={eventKey} className="sequence-layer sequence-opening" aria-live="polite">
        <div className="opening-panel is-left" />
        <div className="opening-panel is-right" />
        <div className="opening-title">
          <span>幻想郷闘乱記</span>
          <strong>開宴</strong>
        </div>
        <div className="opening-deal" aria-label="角色卡发放">
          {state.players.map((entry, index) => (
            <span
              className="opening-card"
              key={entry.id}
              style={
                {
                  "--deal-x": `${(index - center) * 82}px`,
                  "--deal-angle": `${(index - center) * 5}deg`,
                  "--deal-delay": `${260 + index * 105}ms`,
                } as CSSProperties
              }
            >
              <i aria-hidden="true">闘</i>
              <small>{entry.name}</small>
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (event.type === "turn_started") {
    return (
      <div key={eventKey} className="sequence-layer sequence-turn" aria-live="polite">
        <div className="turn-ribbon">
          <span>第 {eventNumber(data, "round") || state.round} 回合</span>
          <strong>{playerName}</strong>
          <small>行动开始</small>
        </div>
      </div>
    );
  }

  if (event.type === "dice_rolled") {
    const d4 = eventNumber(data, "d4");
    const d6 = eventNumber(data, "d6");
    const sum = eventNumber(data, "sum") || d4 + d6;
    const cause = eventText(data, "cause");
    const card = eventText(data, "card_id");
    const outcomes = diceOutcomes.map((outcome) => {
      const result = eventRecord(outcome.data);
      const target = state.players.find((entry) => entry.id === eventText(result, "player"))?.name ?? eventText(result, "player");
      return `${target} · ${outcome.type === "heal" ? "回复" : "伤害"} ${eventNumber(result, "amount")}`;
    });
    return (
      <DiceWindow
        diceKey={eventKey} playerName={`${playerName}${card ? ` · ${cardName(card)}` : cause === "attack" ? " · 攻击判定" : ""}`} d4={d4} d6={d6}
        className={cause === "setup" ? "is-setup" : cause === "attack" ? "is-attack-roll" : ""}
        label={cause === "attack" ? outcomes.join("\n") || "等待反应与伤害结算" : `合计 ${sum}`}
      />
    );
  }

  if (event.type === "move") {
    return (
      <div key={eventKey} className="sequence-layer sequence-move" aria-live="polite">
        <div className="move-route">
          <span className="move-piece" aria-hidden="true">♟</span>
          <strong>{playerName}</strong>
          <small>{eventText(data, "cause") === "adjacent" ? "相邻移动" : "移动"}</small>
        </div>
      </div>
    );
  }

  if (event.type === "damage" || event.type === "heal") {
    const amount = eventNumber(data, "amount");
    const d4 = eventNumber(data, "d4");
    const d6 = eventNumber(data, "d6");
    if (event.type === "damage" && eventText(data, "cause") === "ability" && (d4 > 0 || d6 > 0)) {
      const cause = eventText(data, "cause");
      return (
        <DiceWindow
          diceKey={eventKey} d4={d4} d6={d6} className="is-attack-roll"
          playerName={`${cause === "attack" ? state.players.find((entry) => entry.id === state.turn.player)?.name ?? "" : playerName} · ${cause === "attack" ? "攻击判定" : "骰子判定"}`}
          label={`造成 ${amount} 点伤害`}
        />
      );
    }
    return (
      <div key={eventKey} className={`table-effect effect-${event.type}`} aria-live="polite">
        <span>{playerName}</span>
        <strong>{event.type === "damage" ? "−" : "＋"}{amount}</strong>
      </div>
    );
  }


  if (event.type === "revealed") {
    const id = eventText(data, "role") || player?.role?.id || "";
    return (
      <div key={eventKey} className="table-effect effect-reveal" aria-live="polite">
        {id && <CardFace id={id} alt="" />}
        <span>{playerName}</span>
        <strong>{player?.role?.name ?? "身份公开"}</strong>
      </div>
    );
  }

  return null;
}
